import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { publicApiClient } from '@/api/public'
import { formatPriceLabel } from '@/lib/pricing'
import { moneyFor } from '@/lib/money'
import { datesFor } from '@/lib/dates'

export default function EmbedSessionsPage() {
  const { slug } = useParams<{ slug: string }>()
  const [params] = useSearchParams()

  const courseIds = params.get('courseIds')?.split(',').filter(Boolean) ?? []
  const primaryColour = params.get('primaryColour') ?? '#6366f1'
  const limit = parseInt(params.get('limit') ?? '10')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-sessions', slug],
    queryFn: () => publicApiClient.getPublicSessions(slug!).then(r => r.data),
    enabled: !!slug,
  })
  const { fmt: fmtMoney } = moneyFor(data?.currency)
  const { formatDate, formatTime } = datesFor(data?.region, data?.timeZoneId)
  const fmtDate = (iso: string) => {
    return formatDate(iso, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
  }
  const fmtTime = (iso: string) => {
    return formatTime(iso)
  }
  const sessions = (data?.sessions ?? [])
    .filter(s => courseIds.length === 0 || courseIds.includes(s.courseId))
    .slice(0, limit)

  const bookUrl = (sessionId: string) =>
    `${window.location.origin}/sessions/${slug}/${sessionId}`

  if (isLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
      <div style={{
        width: 20, height: 20, borderRadius: '50%',
        border: `2px solid ${primaryColour}`,
        borderTopColor: 'transparent',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )

  if (isError || !data) return (
    <div style={{ textAlign: 'center', padding: '2rem', fontFamily: 'system-ui, sans-serif' }}>
      <p style={{ color: '#94a3b8', fontSize: 14 }}>Sessions unavailable.</p>
    </div>
  )

  if (sessions.length === 0) return (
    <div style={{ textAlign: 'center', padding: '2.5rem 1.5rem', fontFamily: 'system-ui, sans-serif' }}>
      {data.tenantLogoUrl && (
        <img
          src={data.tenantLogoUrl}
          alt={data.tenantName}
          style={{ height: 40, maxWidth: 160, objectFit: 'contain', margin: '0 auto 1rem' }}
        />
      )}
      <p style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', marginBottom: '0.4rem' }}>
        {data.tenantName}
      </p>
      <p style={{ fontSize: 13, color: '#94a3b8' }}>
        No upcoming sessions at the moment — check back soon.
      </p>
    </div>
  )

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif', padding: '0.75rem', boxSizing: 'border-box' }}>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .cs-card { border: 1px solid #e2e8f0; border-radius: 10px; padding: 1rem; margin-bottom: 0.75rem; background: #fff; }
        .cs-card:last-child { margin-bottom: 0; }
        .cs-title { font-size: 15px; font-weight: 600; color: #1e293b; margin-bottom: 0.25rem; }
        .cs-desc { font-size: 13px; color: #64748b; margin-bottom: 0.6rem; line-height: 1.4; }
        .cs-meta { display: flex; flex-wrap: wrap; gap: 0.5rem 1rem; margin-bottom: 0.75rem; }
        .cs-meta-item { display: flex; align-items: center; gap: 0.3rem; font-size: 13px; color: #64748b; }
        .cs-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.5rem; }
        .cs-price { font-size: 16px; font-weight: 700; color: #1e293b; }
        .cs-free { font-size: 13px; color: #94a3b8; }
        .cs-places { font-size: 12px; color: #94a3b8; }
        .cs-full { font-size: 12px; font-weight: 600; color: #d97706; background: #fef3c7; border-radius: 4px; padding: 2px 8px; }
        .cs-btn { display: inline-block; padding: 0.45rem 1rem; border-radius: 6px; font-size: 13px; font-weight: 600; color: #fff; text-decoration: none; transition: opacity 0.15s; }
        .cs-btn:hover { opacity: 0.85; }
      `}</style>

      {sessions.map(s => (
        <div key={s.id} className="cs-card">
          <div className="cs-title">{s.courseName}</div>
          {s.courseDescription && <div className="cs-desc">{s.courseDescription}</div>}
          <div className="cs-meta">
            <span className="cs-meta-item">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              {fmtDate(s.startsAt)}
            </span>
            <span className="cs-meta-item">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              {fmtTime(s.startsAt)}{s.endsAt ? ` – ${fmtTime(s.endsAt)}` : ''}
            </span>
            {s.deliveryMethod === 'Online' ? (
              <span className="cs-meta-item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 010 20 15.3 15.3 0 010-20z"/></svg>
                Online
              </span>
            ) : s.venue && (
              <span className="cs-meta-item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {s.venue.name}{s.venue.city ? `, ${s.venue.city}` : ''}
              </span>
            )}
          </div>
          <div className="cs-footer">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem' }}>
              {(() => {
                const label = formatPriceLabel(s.pricingModel, s.price, s.flatFeeAmount, s.flatFeeCapacity, fmtMoney)
                return label ? <span className="cs-price">{label}</span> : <span className="cs-free">Free</span>
              })()}
              {s.availablePlaces === 0
                ? <span className="cs-full">Fully booked</span>
                : <span className="cs-places">{s.availablePlaces} place{s.availablePlaces !== 1 ? 's' : ''} left</span>}
            </div>
            {s.availablePlaces > 0 && (
              <a
                href={bookUrl(s.id)}
                target="_blank"
                rel="noreferrer"
                className="cs-btn"
                style={{ backgroundColor: primaryColour }}
              >
                Book now
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
